'use client';

import { motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';

/**
 * CinematicScene — общий каркас для cinematic-сцен (5, 8, 9, 10, 11).
 *
 * Mobile: image сверху (фикс 55vh) + текст ниже на solid dark bg.
 * Desktop: full-bleed image на весь экран + gradient overlay + текст слева.
 *
 * imageObjectPosition по умолчанию "75% 50%" — у наших иллюстраций сюжет
 *  правее центра, на узком экране нужно правое выравнивание чтобы маскот
 *  оставался в кадре. На десктопе full-bleed, position не критичен (картинка
 *  показывается целиком в широком кадре).
 */
export default function CinematicScene({
  imageSrc,
  imageAlt = '',
  imagePriority = false,
  imageObjectPosition = '75% 50%',
  children,
}: {
  imageSrc: string;
  /** alt для SEO/accessibility. Пустая строка = декоративное. */
  imageAlt?: string;
  /** priority loading — для самой первой cinematic-картинки на странице. */
  imagePriority?: boolean;
  /** CSS object-position для image. Дефолт '75% 50%' — слегка правее центра
   *  (у иллюстраций сюжет правее середины). Если у конкретной картинки
   *  композиция иная — переопределить через проп. */
  imageObjectPosition?: string;
  children: React.ReactNode;
}) {
  const reduced = useReducedMotion() ?? false;

  return (
    <section className="relative w-full bg-[#1a1410] min-h-screen overflow-hidden flex flex-col sm:block">
      {/* Image */}
      <div className="relative w-full h-[55vh] shrink-0 sm:absolute sm:inset-0 sm:h-auto">
        <Image
          src={imageSrc}
          alt={imageAlt}
          fill
          sizes="100vw"
          priority={imagePriority}
          className="object-cover"
          style={{ objectPosition: imageObjectPosition }}
        />
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-[#1a1410] sm:hidden"
        />
      </div>

      <div
        aria-hidden
        className="hidden sm:block absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent z-10"
      />

      <motion.div
        initial={reduced ? false : { opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-20 flex-1 px-5 py-10 sm:py-0 sm:absolute sm:inset-0 sm:min-h-screen sm:flex sm:items-center sm:px-8"
      >
        <div className="max-w-6xl mx-auto w-full">
          <div className="max-w-xl">{children}</div>
        </div>
      </motion.div>
    </section>
  );
}
